// Inline license required view — shown as page content in the Dashboard
// when the signed-in user has no active license.
// Presents the same purchase options as the paywall modal but without
// any backdrop, fixed positioning, or close button.

import { Check, CreditCard, Globe, Loader2, Smartphone } from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";
import { useLicense } from "../hooks/useLicense";

export default function LicenseRequired() {
  const { purchaseWeb } = useLicense();
  const [isLoading, setIsLoading] = useState(false);

  const handlePurchaseWeb = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      await purchaseWeb.mutateAsync();
      // User will be redirected to Stripe Checkout
    } catch (error: unknown) {
      toast.error(
        error instanceof Error ? error.message : "Failed to start purchase",
      );
      setIsLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl py-8">
      {/* Header */}
      <div className="mb-4 flex items-center justify-center">
        <CreditCard className="h-12 w-12 text-yellow-600 dark:text-yellow-400" />
      </div>

      <h2 className="mb-2 text-center text-2xl font-bold text-gray-800 dark:text-gray-100">
        One-Time Purchase Required
      </h2>
      <p className="mb-12 text-center text-gray-600 dark:text-gray-400">
        Purchase a license to create and manage a household
      </p>

      {/* Purchase Options */}
      <div className="mb-6 flex flex-col gap-12 md:flex-row md:items-stretch">
        {/* Web License Option */}
        <div className="flex flex-1 flex-col rounded-lg border-2 border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
          <div className="mb-3 flex items-start gap-3">
            <Globe className="mt-1 h-6 w-6 shrink-0 text-blue-500" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                Web License
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                For web and PWA usage
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-800 dark:text-gray-100">
                $9.99
              </div>
              <div className="text-xs text-gray-500">one-time</div>
            </div>
          </div>
          <ul className="mb-4 ml-9 space-y-2 text-sm">
            <FeatureItem text="Works in any browser" />
            <FeatureItem text="Install as a Progressive Web App (PWA)" />
            <FeatureItem text="Create and manage your household" />
            <FeatureItem text="Invite up to 5 household members (free for them)" />
            <FeatureItem text="Real-time sync across devices" />
            <FeatureItem text="Recipe search by ingredients" />
          </ul>
          <div className="mt-auto pt-4">
            <button
              type="button"
              onClick={handlePurchaseWeb}
              disabled={isLoading || purchaseWeb.isPending}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 py-3 font-medium text-white transition hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading || purchaseWeb.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin text-white" />
              ) : (
                <Globe className="h-4 w-4" />
              )}
              {isLoading || purchaseWeb.isPending
                ? "Processing..."
                : "Purchase Web License"}
            </button>
          </div>
        </div>

        {/* App License Option */}
        <div className="relative flex flex-1 flex-col rounded-lg border-2 border-blue-500 bg-blue-50 p-5 dark:border-blue-600 dark:bg-blue-900/20">
          <div className="absolute -top-3 left-4 rounded-full bg-blue-500 px-3 py-1 text-xs font-bold text-white">
            BEST VALUE
          </div>
          <div className="mb-3 flex items-start gap-3">
            <Smartphone className="mt-1 h-6 w-6 shrink-0 text-blue-500" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                Native App License
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Available on Google Play Store
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-800 dark:text-gray-100">
                $14.99
              </div>
              <div className="text-xs text-gray-500">one-time</div>
            </div>
          </div>
          <ul className="mb-4 ml-9 space-y-2 text-sm">
            <FeatureItem text="Everything in Web License" />
            <FeatureItem text="Native Android app" />
            <FeatureItem text="Better offline support" />
            <FeatureItem text="Faster performance" />
            <FeatureItem text="Unlocks web app usage" highlighted />
          </ul>
          <div className="mt-auto pt-4">
            <a
              href="https://play.google.com/store/apps/details?id=com.hillardworks.pantry"
              target="_blank"
              rel="noopener noreferrer"
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 py-3 font-medium text-white transition-colors hover:bg-blue-700"
            >
              <Smartphone className="h-4 w-4" />
              Get on Google Play
            </a>
          </div>
        </div>
      </div>

      {/* Info text */}
      <div className="space-y-2 text-center">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          💡 <strong>Invited members always FREE</strong> — Join via web at no
          cost
        </p>
        <p className="text-xs text-gray-500">
          One-time purchase &bull; No subscriptions &bull; Lifetime access
        </p>
      </div>
    </div>
  );
}

function FeatureItem({
  text,
  highlighted = false,
}: {
  text: string;
  highlighted?: boolean;
}) {
  return (
    <li className="flex items-start gap-2">
      <Check
        className={`mt-0.5 h-4 w-4 shrink-0 ${
          highlighted ? "text-blue-500" : "text-green-500"
        }`}
      />
      <span
        className={
          highlighted ? "font-semibold text-blue-600 dark:text-blue-400" : ""
        }
      >
        {text}
      </span>
    </li>
  );
}
